import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackVisit } from '@lib/analytics';
import { useAuth } from '@lib/useAuth';

function deriveCountryFromLocale(): string | null {
  const nav =
    typeof navigator !== 'undefined' ? (navigator as Navigator) : null;
  if (!nav) return null;

  const primaryLocale = nav.language ?? '';
  const locales = [...(nav.languages ?? []), primaryLocale]
    .filter(Boolean)
    .map((locale) => locale.replace('_', '-'));

  for (const locale of locales) {
    const [, region] = locale.split('-');
    if (region && region.length >= 2) {
      return region.toUpperCase();
    }
  }

  return null;
}

function normalizeUtm(value: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed?.length ? trimmed : null;
}

export default function VisitTracker(): null {
  const location = useLocation();
  const { user } = useAuth();
  const lastHref = useRef<string | null>(null);
  const lastTrackedUserId = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const currentHref = window.location.href;
    // If we've already tracked this exact href for the same user, skip.
    const currentUserId = user?.id ?? null;
    if (
      lastHref.current === currentHref &&
      lastTrackedUserId.current === currentUserId
    ) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const utmSource = normalizeUtm(params.get('utm_source'));

    const documentReferrer =
      typeof document !== 'undefined' ? normalizeUtm(document.referrer) : null;

    // Do not track visits coming from Vercel preview domains (always check actual document.referrer)
    if (
      documentReferrer &&
      documentReferrer.toLowerCase().includes('vercel.app')
    ) {
      lastHref.current = currentHref;
      return;
    }

    // If we don't have a previous href or a document referrer, fall back to
    // the current href so direct page visits are recorded as a visit to that
    // page (useful for pages like /breathing which are often visited directly).
    const previousHref =
      lastHref.current ?? documentReferrer ?? currentHref ?? null;

    trackVisit({
      referrer: previousHref,
      utm_source: utmSource,
      country: deriveCountryFromLocale(),
      device: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      occurred_at: new Date().toISOString(),
      user_id: currentUserId,
    });

    lastHref.current = currentHref;
    lastTrackedUserId.current = currentUserId;
  }, [location.key, location.pathname, location.search, user?.id]);

  return null;
}
